import { useCallback, useMemo } from 'react';
import { useAppSelector } from '@castlery/shared-redux-store';
import { selectOrder } from '@castlery/modules-order-domain';
import { makeCookiesHandle } from '@castlery/shared-persistence-kit';
import { isOutdated } from '@castlery/modules-cms-services';
import type { Order } from '@castlery/types';
import { dyRecommendationsApi } from '@castlery/modules-dy-domain';
import type { CampaignsRequestOptions } from '@castlery/modules-dy-domain';

export interface PriceBreakDiscount {
  limit: number;
  label: string;
  icon: string;
}

export interface CampaignMessage {
  startDate: string;
  endDate: string;
  link: string;
  campaignName: string;
  discounts: PriceBreakDiscount[];
  discountStrategy?: {
    off: number;
    increment: number;
    minLimit: number;
    maxLimit: number;
  };
}

export type CampaignMessages = CampaignMessage[];

export const usePriceBreakCampaign = () => {
  const order = useAppSelector(selectOrder) as Order;
  const insiderIndex = makeCookiesHandle('castlery_insider', false)().getItem();
  const { item_total_exclude_gift: rawItemTotal = 0 } = order ?? {};
  const itemTotal = Number(rawItemTotal);

  // DY Campaign Messages
  const priceBreakCampaignName = 'Cart Messagings Configure';
  const { data } = dyRecommendationsApi.useGetDyCampaignsQuery({
    selectors: [priceBreakCampaignName],
    pageType: 'OTHER',
    tagTypes: 'Gift',
    campaignNames: [priceBreakCampaignName],
  } as CampaignsRequestOptions);

  const { choices } = data || {};

  let dyApiCampaigns: any = {};

  if (Array.isArray(choices) && choices.length > 0) {
    dyApiCampaigns = choices.reduce((acc: any, choice: any) => {
      if (choice.decisionId === null) return acc;
      return {
        ...acc,
        [choice.name]: {
          campaignName: choice.name,
          data: choice.variations?.[0]?.payload?.data || null,
          variationId: choice.variations?.[0]?.id,
          decisionId: choice.decisionId,
        },
      };
    }, {});
  }

  const priceBreakCampaigns = useMemo(() => {
    if (!dyApiCampaigns?.[priceBreakCampaignName]?.data) {
      return [];
    }

    const data = dyApiCampaigns[priceBreakCampaignName].data as CampaignMessages;
    if (!Array.isArray(data)) {
      return [];
    }

    return data;
  }, [dyApiCampaigns, priceBreakCampaignName]);

  // 确定当前适用的价格阶梯活动
  const priceBreakCampaign = useMemo(() => {
    if (insiderIndex && ['0', '1', '2', '3'].includes(insiderIndex)) {
      return priceBreakCampaigns[parseInt(insiderIndex)];
    }
    return priceBreakCampaigns.find((campaign) => !isOutdated(campaign.startDate, campaign.endDate));
  }, [insiderIndex, priceBreakCampaigns]);

  const calcDiscount = useCallback(
    ({ off, increment, minLimit, maxLimit }: any) => {
      if (
        typeof off !== 'number' ||
        off <= 0 ||
        typeof increment !== 'number' ||
        increment <= 0 ||
        minLimit > maxLimit
      ) {
        return null;
      }

      if (itemTotal < maxLimit) {
        const m = Math.floor(itemTotal / increment) + 1;
        return {
          icon: 'check-circle',
          limit: m * increment,
          label: `$${off * m} off`,
        };
      }
      return null;
    },
    [itemTotal]
  );
  let currentPriceBreakCampaign = null;

  if (priceBreakCampaign) {
    if (priceBreakCampaign?.discountStrategy) {
      currentPriceBreakCampaign = calcDiscount(priceBreakCampaign.discountStrategy);
    } else {
      currentPriceBreakCampaign = priceBreakCampaign?.discounts?.find((discount) => itemTotal < discount.limit);
    }
  }

  return {
    currentPriceBreakCampaign,
    priceBreakCampaign,
  };
};
